import { WORK_STATE_LABELS, WORK_STATES, type WorkState } from '@scc/shared';
import type { WorkItem } from '../api';
import { Badge, Empty, RelativeTime } from './ui';

/**
 * H6.6 · Tablero de las diez columnas.
 *
 * Las diez columnas se muestran **siempre**, incluso vacías. Ocultar las vacías
 * haría que el tablero cambiara de forma según los datos y que nadie supiera si
 * «En pruebas» no aparece porque no hay nada o porque no existe esa fase.
 *
 * El tablero es de lectura: mover una tarjeta arrastrándola exigiría decidir si
 * eso convierte su estado en manual, y esa decisión debe ser explícita. Se cambia
 * desde el panel de detalle, donde se ve la consecuencia.
 */

const STATE_TONE: Record<WorkState, 'neutral' | 'good' | 'warn' | 'bad'> = {
  backlog: 'neutral',
  ready: 'neutral',
  in_development: 'warn',
  in_review: 'warn',
  in_testing: 'warn',
  ready_for_prod: 'good',
  in_production: 'good',
  blocked: 'bad',
  done: 'good',
  cancelled: 'neutral',
};

export function Kanban({
  items,
  onSelect,
}: {
  items: WorkItem[];
  onSelect?: (item: WorkItem) => void;
}): JSX.Element {
  if (items.length === 0) {
    return <Empty>Ningún item de trabajo registrado todavía.</Empty>;
  }

  return (
    // Scroll horizontal propio: diez columnas no caben en una pantalla y el
    // cuerpo de la página nunca debe scrollear en horizontal.
    <div className="overflow-x-auto pb-2">
      <div className="flex min-w-max gap-2">
        {WORK_STATES.map((state) => {
          const column = items.filter((item) => item.state === state);
          return (
            <section key={state} className="w-56 shrink-0">
              <header className="mb-1.5 flex items-baseline justify-between gap-2 border-b border-[var(--color-border-subtle)] pb-1">
                <h3 className="text-[11px] font-semibold uppercase tracking-wide text-[var(--color-ink-muted)]">
                  {WORK_STATE_LABELS[state]}
                </h3>
                <span className="text-[11px] tabular-nums text-[var(--color-ink-muted)]">
                  {column.length}
                </span>
              </header>

              {column.length === 0 ? (
                <p className="px-1 py-2 text-[11px] text-[var(--color-ink-muted)]">—</p>
              ) : (
                <ul className="space-y-1.5">
                  {column.map((item) => (
                    <li key={item.id}>
                      <Card item={item} onSelect={onSelect} />
                    </li>
                  ))}
                </ul>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
}

function Card({
  item,
  onSelect,
}: {
  item: WorkItem;
  onSelect?: (item: WorkItem) => void;
}): JSX.Element {
  const content = (
    <>
      <div className="flex items-start justify-between gap-1.5">
        <span className="text-[12px] font-medium leading-snug">{item.title}</span>
        <Badge tone={item.priority === 'P0' ? 'bad' : item.priority === 'P1' ? 'warn' : 'neutral'}>
          {item.priority}
        </Badge>
      </div>

      <div className="mt-1.5 flex flex-wrap items-center gap-1 text-[10px] text-[var(--color-ink-muted)]">
        <span>{item.kind === 'bug' ? 'error' : item.kind === 'feature' ? 'func.' : 'tarea'}</span>
        {item.trackCode && <span>· {item.trackCode}</span>}
        {item.assigneeName && <span>· {item.assigneeName}</span>}
        {item.branchName && <span className="font-mono">· {item.branchName}</span>}
      </div>

      {(item.isOverdue || item.closedWithoutEvidence || item.origin === 'github') && (
        <div className="mt-1.5 flex flex-wrap gap-1">
          {item.isOverdue && <Badge tone="bad">vencida</Badge>}
          {/* H6.9: cerrado sin evidencia es una afirmación sin respaldo. */}
          {item.closedWithoutEvidence && (
            <span title="Marcado como completado pero sin ninguna evidencia enlazada">
              <Badge tone="warn">sin evidencia</Badge>
            </span>
          )}
          {item.origin === 'github' && (
            <span title="Viene de un issue de GitHub: se gestiona allí">
              <Badge>GitHub</Badge>
            </span>
          )}
        </div>
      )}

      <div className="mt-1.5 flex items-center justify-between gap-2 text-[10px] text-[var(--color-ink-muted)]">
        <span>
          {item.stateSource === 'derived' ? (
            <span title="El estado lo calcula el derivador a partir del código">derivado</span>
          ) : (
            <span title="Alguien fijó este estado a mano: el derivador no lo toca">manual</span>
          )}
        </span>
        {item.targetDate ? (
          <span className={item.isOverdue ? 'text-red-700 dark:text-red-400' : ''}>
            {item.targetDate}
          </span>
        ) : (
          <RelativeTime iso={item.updatedAt} />
        )}
      </div>
    </>
  );

  const className =
    `w-full rounded border border-[var(--color-border-subtle)] bg-[var(--color-surface-muted)] px-2 py-1.5 text-left ` +
    (onSelect ? 'transition-colors hover:border-[var(--color-ink-muted)]' : '');

  return onSelect ? (
    <button type="button" onClick={() => onSelect(item)} className={className}>
      {content}
    </button>
  ) : (
    <div className={className}>{content}</div>
  );
}

/** Resumen por estado, para cuando el tablero es demasiado ancho. */
export function StateSummary({ items }: { items: WorkItem[] }): JSX.Element {
  return (
    <ul className="flex flex-wrap gap-1.5">
      {WORK_STATES.filter((state) => items.some((item) => item.state === state)).map((state) => (
        <li key={state}>
          <Badge tone={STATE_TONE[state]}>
            {WORK_STATE_LABELS[state]}: {items.filter((item) => item.state === state).length}
          </Badge>
        </li>
      ))}
    </ul>
  );
}
